import type { FilesContent, FilesStatus } from "component/filesContainer/types";
import type { PickerVehicleDataFilesType } from "pages/pickers/types";
import {
  MOTORIZED_VEHICLES_TAGS,
  VEHICLE_MOTORIZED_DOCUMENT_TAGS,
  type VehicleTagType,
} from "utils/constants";
import type { PickerFileApiRow, PickerFileContentInput } from "utils/files";
import {
  doesPickerFileRowIndicateUpload,
  normalizePickerFileRow,
  normalizePickerFileSection,
  normalizePickerFileTag,
} from "utils/files";

type VehicleFileContentRow =
  FilesContent<PickerVehicleDataFilesType>["content"][number];

const deriveFilesStatusFromUploadFlags = (
  rows: PickerFileApiRow[]
): FilesStatus => {
  if (rows.length === 0) return "EMPTY";
  const uploadedFlags = rows.map((row) => doesPickerFileRowIndicateUpload(row));
  if (uploadedFlags.every(Boolean)) return "COMPLETED";
  if (uploadedFlags.some(Boolean)) return "PENDING";
  return "EMPTY";
};

export const mergeMotorizedVehicleDocumentSlots = (
  vehicleType: VehicleTagType | undefined,
  vehicleFiles: FilesContent<PickerVehicleDataFilesType>
): FilesContent<PickerVehicleDataFilesType> => {
  const responseIncludesMotorizedDocTag = vehicleFiles.content.some((row) =>
    (VEHICLE_MOTORIZED_DOCUMENT_TAGS as readonly string[]).includes(
      normalizePickerFileTag(row.tag)
    )
  );

  const shouldApplyMotorizedSlotTemplate =
    (!!vehicleType && MOTORIZED_VEHICLES_TAGS.includes(vehicleType)) ||
    responseIncludesMotorizedDocTag;

  if (!shouldApplyMotorizedSlotTemplate) {
    return normalizePickerFileSection(vehicleFiles);
  }

  const vehicleFileRowsByNormalizedTag = new Map<
    string,
    PickerFileContentInput<PickerVehicleDataFilesType>
  >(
    vehicleFiles.content.map((row) => {
      const typed =
        row as PickerFileContentInput<PickerVehicleDataFilesType>;
      return [normalizePickerFileTag(typed.tag), typed];
    })
  );

  const requiredMotorizedTags = [
    ...VEHICLE_MOTORIZED_DOCUMENT_TAGS,
  ] as PickerVehicleDataFilesType[];

  const mergedRows: VehicleFileContentRow[] = [];

  for (const requiredTag of requiredMotorizedTags) {
    const existingRow = vehicleFileRowsByNormalizedTag.get(
      normalizePickerFileTag(requiredTag)
    );
    const rowWithRequiredTag = existingRow
      ? ({ ...existingRow, tag: requiredTag } as PickerFileContentInput<PickerVehicleDataFilesType>)
      : null;

    mergedRows.push(
      rowWithRequiredTag
        ? (normalizePickerFileRow(rowWithRequiredTag) as VehicleFileContentRow)
        : { tag: requiredTag, isUpload: false, error: "NONE" }
    );
  }

  for (const row of vehicleFiles.content) {
    const normalizedTag = normalizePickerFileTag(
      row.tag
    ) as PickerVehicleDataFilesType;
    if (!requiredMotorizedTags.includes(normalizedTag)) {
      mergedRows.push(
        normalizePickerFileRow(
          row as PickerFileContentInput<PickerVehicleDataFilesType>
        ) as VehicleFileContentRow
      );
    }
  }

  return {
    status: deriveFilesStatusFromUploadFlags(mergedRows),
    content: mergedRows,
  };
};
